import * as React from 'react';
import { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { GlobalApplicationState } from 'globalApplicationState';
import * as actions from 'modules/posts/actionCreator';
import { Comment, ImageScale, PostView as IPostView } from '../../models';
import { postsApi } from 'api/instances';
import ErrorSnackbar from "modules/common/components/snackbars/errorSnackbar";
import Loading from "modules/common/components/loading";
import useIsMounted from 'modules/common/hooks/useIsMounted';
import useIsFirstRender from 'modules/common/hooks/useIsFirstRender';
import PostViewContent from './postViewContent';
import { useDefaultLocale } from 'modules/common/hooks/useDefaultLocale';
import ShareButtonv2 from "modules/common/components/buttons/shareButtonv2";
import PublicShareScreen from "modules/sharing/publicShareScreen";
import InternalShareScreen from "modules/sharing/internalShareScreen";
import { ContentType as ShareContentType } from "modules/sharing/models";
import { useIsAuthenticated } from "@azure/msal-react";

import './postView.sass';

const PostView: React.FunctionComponent<PropsWithRedux> = (props) => {
    const [activeLcid, setActiveLcid] = useState<string>(props.currentUserPreferredLCID);
    const [post, setPost] = useState<IPostView>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const postRef = React.useRef<HTMLInputElement | null>(null);
    const [publicShareOpen, setPublicShareOpen] = useState<boolean>(false);
    const [internalShareOpen, setInternalShareOpen] = useState<boolean>(false);
    const isAuthenticated = useIsAuthenticated();
    const tenantId = props.tenantId || props.configTenantId || "";

    const isMounted = useIsMounted();
    const isFirstRender = useIsFirstRender();
    useDefaultLocale();

    useEffect(() => {
        /**
         * Get a public post
         * @param isInitialLoad
         */
        const getPublicPost = async (isInitialLoad: boolean): Promise<IPostView | null> => {
            if (!props.tenantId || !props.postId) return null;

            const response: IPostView = await postsApi.GetPublicPost(props.tenantId, props.postId, ImageScale.Modal);
            if (response && isInitialLoad)
                await postsApi.SendPublicPostOpenedEvent(props.tenantId, response.id, response.defaultLCID);

            return response;
        };

        /**
         * Get a private post
         * @param initialLoad
         */
        const getPrivatePost = async (isInitialLoad: boolean): Promise<IPostView | null> => {
            if (!props.postId) return null;

            const response = await postsApi.GetPost(props.postId, ImageScale.Modal);
            if (response && isInitialLoad)
                await postsApi.SendPostOpenedEvent(response.id, response.defaultLCID);

            return response;
        };

        /**
         * Decide whether we need to get a private or public post and fetch
         * @param initialLoad
         */
        const getPost = async (initialLoad: boolean) => {
            props.clearFetchPost();
            let newPost: IPostView | null;

            try {
                // get the post
                if (props.isPublic && props.tenantId)
                    newPost = await getPublicPost(initialLoad);
                else
                    newPost = await getPrivatePost(initialLoad);

                // if post got, update state
                if (newPost && isMounted()) {
                    setPost(newPost);
                    setActiveLcid(
                        newPost.translatedContent?.[props.currentUserPreferredLCID]
                            ? props.currentUserPreferredLCID
                            : newPost.defaultLCID
                    );
                }
            } catch (error) { }
            finally {
                if (isMounted())
                    setIsLoading(false);
            }
        };

        if (isFirstRender || props.shouldFetch)
            getPost(isFirstRender);

        return () => {
            if (post) {
                if (props.isPublic && props.tenantId)
                    postsApi.SendPublicPostClosedEvent(props.tenantId!, post.id, post.defaultLCID);
                else
                    postsApi.SendPostClosedEvent(post.id, post.defaultLCID);
            }
        };
    }, [isFirstRender, isMounted, post, props]);

    const onUpdateComments = (comments: Comment[]) => {
        if (!post) return;

        setPost({ ...post, comments });
    };

    const onUpdateCommentSubscriptions = (isPostNotificationsActive: boolean) => {
        if (!post) return;

        setPost({ ...post, isPostNotificationsActive });
    };

    return (
        <div className="post-view" ref={postRef} style={{position: "relative", overflowY: publicShareOpen || internalShareOpen ? "hidden" : "auto"}}>
            {isLoading && <Loading />}
            {
                !isLoading && !post &&
                <div className="no-post-data">Unfortunately, this post is no longer available.</div>
            }
            {
                !isLoading && post &&
                <PostViewContent 
                    post={post}
                    defaultLCID={activeLcid}
                    onUpdateCommentSubscriptions={onUpdateCommentSubscriptions}
                    onUpdateComments={onUpdateComments}
                    shareButtonComponent={
                        <ShareButtonv2
                            isPublic={post.postType === "public"}
                            toggleInternalShare={(toSet) => { 
                                postRef.current?.scrollTo(0, 0);
                                setInternalShareOpen(toSet)
                            }}
                            togglePublicShare={(toSet) => {
                                postRef.current?.scrollTo(0, 0);
                                setPublicShareOpen(toSet)
                            }}
                            isAuthenticated={isAuthenticated}
                        />
                    }
                    shareScreenComponent={<>
                        {
                            publicShareOpen && 
                            tenantId !== undefined &&
                            <PublicShareScreen 
                                onCloseDialog={() => setPublicShareOpen(false)}
                                tenantName={props.tenantName}
                                tenantId={tenantId}
                                contentTitle={post.title}
                                isAuthenticated={isAuthenticated}
                                contentType={ShareContentType.Post}
                                contentId={post.id}
                                customShareUrl={post.customPublicUrl ?? ""}
                            />
                        }
                        {
                            internalShareOpen &&
                            <InternalShareScreen 
                                onCloseDialog={() => setInternalShareOpen(false)}
                                tenantName={props.tenantName}
                                contentTitle={post.title}
                                contentId={post.id}
                                contentStyling={{marginTop: "40%"}}
                                contentType={ShareContentType.Post}
                                isPublic={post.postType === "public"}
                            />
                        }
                    </>}
                />
            }
            <ErrorSnackbar errorMessage={props.errorMessage} clearErrorMessage={props.clearErrorMessage} />
        </div>
    );
}

interface ComponentProps {
    isPublic?: boolean;
    postId: string;
    tenantId?: string;
}

const DEFAULT_LCID = "en-us";
const connector = connect(
    (state: GlobalApplicationState, ownProps: ComponentProps) => ({
        ...ownProps,
        errorMessage: state.posts.postView.errorMessage,
        shouldFetch: state.posts.postView.shouldFetch,
        currentUserPreferredLCID: state.settings.currentUser.preferredLCID,
        configTenantId: state.settings.clientSettings.id,
        tenantName: state.settings.clientSettings.displayName,
    }),
    {
        clearErrorMessage: actions.clearPostViewErrorMessage,
        clearFetchPost: actions.clearFetchPost,
    }
);
type PropsWithRedux = ConnectedProps<typeof connector>;
export default connector(PostView);
